import { FC, Fragment } from 'react';
import { Text } from '@mantine/core';
import { IconArrowRight, IconArrowsLeftRight, IconLock } from '@tabler/icons-react';
import { INTEGRATION_SYNC_ROWS, SyncDirection, syncDirectionNote } from '@/PageComponents/Onboarding/onboarding-config';
import styles from '@/PageComponents/Onboarding/components/IntegrationSync.module.css';

/**
 * The pieces of "Connect your data" (the dashboard's modal): how connecting works, the privacy note
 * (shared with the integrations settings page) and how each kind of data syncs.
 */

const DirectionPill: FC<{ direction: SyncDirection; partnerName: string }> = ({ direction, partnerName }) => (
    <span className={styles.pill}>
        {direction === 'both' ? <IconArrowsLeftRight size={20} stroke={2} /> : <IconArrowRight size={20} stroke={2} />}
        {direction === 'both' ? 'Both ways' : `To ${partnerName}`}
    </span>
);

export const ConnectSteps: FC<{ partnerName: string }> = ({ partnerName }) => {
    const steps = [
        { title: `Sign into ${partnerName}`, text: `You will sign in directly into ${partnerName} using your username and password.` },
        { title: 'Choose what syncs', text: 'You will be redirected to ServCraft, where you can define how your data syncs across.' },
        { title: 'Your data connects', text: 'Data loading takes between 10 minutes and a couple of hours depending on data size.' },
    ];
    return (
        <ol className={styles.steps}>
            {steps.map((step, i) => (
                <Fragment key={step.title}>
                    {i > 0 && <li aria-hidden className={styles.stepLine} />}
                    <li className={styles.step}>
                        <span className={styles.stepNumber}>{i + 1}</span>
                        <div>
                            <Text className={styles.stepTitle}>{step.title}</Text>
                            <Text className={styles.stepText}>{step.text}</Text>
                        </div>
                    </li>
                </Fragment>
            ))}
        </ol>
    );
};

export const PrivacyNote: FC = () => (
    <Text className={styles.privacy}><IconLock size={24} stroke={1.5} />Your password stays private. Disconnect at any time.</Text>
);

export const SyncTable: FC<{ partnerName: string }> = ({ partnerName }) => (
    <div className={styles.table} role="table" aria-label={`How data syncs between ServCraft and ${partnerName}`}>
        <div className={`${styles.row} ${styles.headerRow}`} role="row">
            <span role="columnheader">Data type</span>
            <span role="columnheader">How data syncs</span>
            <span role="columnheader">What you can do</span>
        </div>
        {INTEGRATION_SYNC_ROWS.map(row => (
            <div key={row.servcraft} className={styles.row} role="row">
                <span role="cell" className={styles.typeCell}>{row.servcraft}</span>
                <span role="cell"><DirectionPill direction={row.direction} partnerName={partnerName} /></span>
                <span role="cell" className={styles.noteCell}>{syncDirectionNote(row.direction, partnerName)}</span>
            </div>
        ))}
    </div>
);

